/**
 * Universal Clipboard
 *
 * The primary ingestion primitive for AOS. All content enters
 * through paste operations with full provenance tracking.
 */

import { createDAGObject } from '../dag/object.js';
import type { DAGStore } from '../dag/store.js';
import type { KeyPair } from '../identity/keys.js';
import { signObject } from '../identity/signing.js';
import { detectMimeType, isAOSSystemType } from './detect.js';
import { chunkContent, reassembleContent, getChunkStatus } from './chunking.js';
import { createTombstoneContent, isTombstone, getTombstoneTarget } from '../content/tombstone.js';
import {
  type ClipboardEntry,
  type ClipboardEntryContent,
  type ClipboardFilter,
  type PasteOptions,
  type ExportGrant,
  type ExportGrantContent,
  type AdminExportLog,
  type AdminExportLogContent,
  type ExportPolicy,
  type ViewPolicy,
  type DeviceProvenance,
  type UserProvenance,
  isClipboardEntry,
  isExportGrant,
  CLIPBOARD_MIME_TYPES,
} from './types.js';

// ============================================================================
// Clipboard Manager
// ============================================================================

/**
 * Configuration for ClipboardManager
 */
export interface ClipboardConfig {
  /** DAG store for persistence */
  dagStore: DAGStore;
  /** User's key pair for signing */
  keyPair: KeyPair;
  /** Device provenance info */
  device: DeviceProvenance;
  /** User provenance info */
  user: UserProvenance;
  /** Callback when new entry arrives (from sync) */
  onNewEntry?: (entry: ClipboardEntry) => void;
}

/**
 * Result of paste operation
 */
export interface PasteResult {
  /** Success status */
  success: boolean;
  /** Entry hash (if successful) */
  entryHash?: string;
  /** Content manifest hash */
  manifestHash?: string;
  /** Detected MIME type */
  mimeType?: string;
  /** Error message (if failed) */
  error?: string;
  /** Whether this is an AOS system type requiring special handling */
  isSystemType?: boolean;
}

/**
 * Result of yank (retrieve) operation
 */
export interface YankResult {
  /** Success status */
  success: boolean;
  /** Retrieved content (if successful) */
  data?: Uint8Array;
  /** Entry metadata */
  entry?: ClipboardEntry;
  /** Error message (if failed) */
  error?: string;
}

/**
 * Export check result
 */
export interface ExportCheckResult {
  /** Whether export is allowed */
  allowed: boolean;
  /** Reason for denial */
  reason?: string;
  /** Export method (owner, grant, admin) */
  method?: 'owner' | 'grant' | 'admin';
}

/**
 * Universal Clipboard Manager
 */
export class ClipboardManager {
  private config: ClipboardConfig;

  constructor(config: ClipboardConfig) {
    this.config = config;
  }

  /**
   * Generate unique entry ID
   */
  private generateEntryId(): string {
    const timestamp = Math.floor(Date.now() / 1000);
    const random = Math.random().toString(36).slice(2, 6);
    return `clip-${timestamp}-${random}`;
  }

  /**
   * Paste content into clipboard
   *
   * This is the primary ingestion method. All content enters AOS via paste.
   */
  async paste(
    data: Uint8Array | string,
    options?: PasteOptions
  ): Promise<PasteResult> {
    try {
      // Convert string to bytes
      const bytes = typeof data === 'string'
        ? new TextEncoder().encode(data)
        : data;

      // Detect MIME type
      const mimeType = detectMimeType(bytes, {
        filename: options?.filename,
        explicitType: options?.type,
      });

      // Check if this is an AOS system type (boot, package, invite, grant)
      const isSystemType = isAOSSystemType(mimeType);

      // Chunk and store content
      const chunkResult = await chunkContent(
        bytes,
        mimeType,
        this.config.keyPair,
        this.config.dagStore
      );

      // Create clipboard entry
      const entryContent: ClipboardEntryContent = {
        version: 1,
        id: this.generateEntryId(),
        date: new Date().toISOString(),
        from: this.config.device,
        by: this.config.user,
        content: {
          type: mimeType,
          dag: chunkResult.manifestHash,
          size: chunkResult.size,
          chunks: chunkResult.chunkCount,
          sha256: chunkResult.sha256,
        },
        meta: {
          label: options?.label,
          tags: options?.tags,
          sourceUrl: options?.sourceUrl,
          filename: options?.filename,
        },
        permissions: {
          export: options?.exportPolicy ?? 'owner',
          view: options?.viewPolicy ?? 'team',
          grants: [],
        },
      };

      // Create and sign entry
      const entryObj = createDAGObject<ClipboardEntryContent>({
        type: 'clipboard.entry',
        creator: this.config.keyPair.publicKeyBase58,
        timestamp: Date.now(),
        parents: [chunkResult.manifestHash],
        content: entryContent,
      });
      const signedEntry = signObject(entryObj, this.config.keyPair);

      // Store entry
      await this.config.dagStore.put(signedEntry);

      return {
        success: true,
        entryHash: signedEntry.hash,
        manifestHash: chunkResult.manifestHash,
        mimeType,
        isSystemType,
      };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Unknown error',
      };
    }
  }

  /**
   * Yank (retrieve) content from clipboard
   *
   * @param hashOrIndex - Entry hash, manifest hash, or numeric index (0 = latest)
   */
  async yank(hashOrIndex: string | number): Promise<YankResult> {
    try {
      let entry: ClipboardEntry | undefined;

      if (typeof hashOrIndex === 'number') {
        // Get by index (0 = latest)
        const entries = await this.list({ limit: hashOrIndex + 1 });
        entry = entries[hashOrIndex];
      } else {
        // Try to get as entry first
        const obj = await this.config.dagStore.get(hashOrIndex);
        if (obj && isClipboardEntry(obj)) {
          entry = obj;
        } else {
          // Try to find entry by manifest hash
          const all = await this.config.dagStore.getAll();
          entry = all.find(
            o => isClipboardEntry(o) && o.content.content.dag === hashOrIndex
          ) as ClipboardEntry | undefined;
        }
      }

      if (!entry) {
        return {
          success: false,
          error: 'Entry not found',
        };
      }

      // Reassemble content from chunks
      const data = await reassembleContent(
        entry.content.content.dag,
        this.config.dagStore
      );

      return {
        success: true,
        data,
        entry,
      };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Unknown error',
      };
    }
  }

  /**
   * List clipboard entries
   */
  async list(filter?: ClipboardFilter): Promise<ClipboardEntry[]> {
    const all = await this.config.dagStore.getAll();

    // Get tombstoned hashes
    const tombstoned = new Set<string>();
    for (const obj of all) {
      if (isTombstone(obj)) {
        tombstoned.add(getTombstoneTarget(obj));
      }
    }

    // Filter to clipboard entries (excluding tombstoned)
    let entries = all.filter(obj => isClipboardEntry(obj) && !tombstoned.has(obj.hash)) as ClipboardEntry[];

    // Apply filters
    if (filter?.from) {
      entries = entries.filter(e => e.content.by.userId === filter.from);
    }

    if (filter?.type) {
      const typePattern = filter.type.replace('*', '.*');
      const regex = new RegExp(`^${typePattern}$`);
      entries = entries.filter(e => regex.test(e.content.content.type));
    }

    if (filter?.after) {
      entries = entries.filter(e => e.timestamp > filter.after!);
    }

    if (filter?.before) {
      entries = entries.filter(e => e.timestamp < filter.before!);
    }

    if (filter?.tags && filter.tags.length > 0) {
      entries = entries.filter(e =>
        e.content.meta.tags?.some(tag => filter.tags!.includes(tag))
      );
    }

    // Sort by timestamp (newest first)
    entries.sort((a, b) => b.timestamp - a.timestamp);

    // Apply pagination
    const offset = filter?.offset ?? 0;
    const limit = filter?.limit ?? 50;
    entries = entries.slice(offset, offset + limit);

    return entries;
  }

  /**
   * Get latest clipboard entry
   */
  async latest(): Promise<ClipboardEntry | null> {
    const entries = await this.list({ limit: 1 });
    return entries[0] ?? null;
  }

  /**
   * Check if user can export content
   */
  async canExport(entryHash: string): Promise<ExportCheckResult> {
    const obj = await this.config.dagStore.get(entryHash);
    if (!obj || !isClipboardEntry(obj)) {
      return { allowed: false, reason: 'Entry not found' };
    }

    const entry = obj as ClipboardEntry;
    const userId = this.config.user.userId;

    // Check 1: Am I the owner?
    if (entry.content.by.userId === userId) {
      return { allowed: true, method: 'owner' };
    }

    // Check 2: Is export policy public?
    if (entry.content.permissions.export === 'public') {
      return { allowed: true, method: 'owner' }; // Public access
    }

    // Check 3: Do I have a valid grant?
    if (entry.content.permissions.export === 'granted') {
      const grant = await this.findValidGrant(
        entry.content.content.dag,
        userId
      );
      if (grant) {
        return { allowed: true, method: 'grant' };
      }
    }

    // Check 4: Admin check would go here (requires admin role verification)
    // For now, we don't implement admin override

    return {
      allowed: false,
      reason: 'Export not permitted. Request a grant from the owner.',
    };
  }

  /**
   * Find a valid export grant for content
   */
  private async findValidGrant(
    contentDag: string,
    recipientId: string
  ): Promise<ExportGrant | null> {
    const all = await this.config.dagStore.getAll();
    const grants = all.filter(isExportGrant);

    const now = Date.now();

    for (const grant of grants) {
      // Check content match
      if (grant.content.contentDag !== contentDag) continue;

      // Check recipient match (or wildcard)
      if (grant.content.to !== '*' && grant.content.to !== recipientId) continue;

      // Check expiration
      if (grant.content.expires && grant.content.expires < now) continue;

      // Check max uses
      if (grant.content.maxUses !== undefined) {
        const useCount = grant.content.useCount ?? 0;
        if (useCount >= grant.content.maxUses) continue;
      }

      return grant;
    }

    return null;
  }

  /**
   * Create an export grant
   */
  async grant(
    contentDag: string,
    recipientId: string,
    options?: {
      expires?: number;
      maxUses?: number;
    }
  ): Promise<string> {
    const grantContent: ExportGrantContent = {
      version: 1,
      action: 'export',
      contentDag,
      from: this.config.user.userId,
      to: recipientId,
      expires: options?.expires,
      maxUses: options?.maxUses,
      useCount: 0,
    };

    const grantObj = createDAGObject<ExportGrantContent>({
      type: 'clipboard.grant',
      creator: this.config.keyPair.publicKeyBase58,
      timestamp: Date.now(),
      parents: [],
      content: grantContent,
    });
    const signedGrant = signObject(grantObj, this.config.keyPair);

    await this.config.dagStore.put(signedGrant);
    return signedGrant.hash;
  }

  /**
   * Revoke an export grant
   *
   * Since DAG is immutable, we create a revocation object that
   * references the original grant.
   */
  async revoke(grantHash: string): Promise<void> {
    // We could create a revocation object, but for simplicity,
    // we just update the grant's use count to max
    // In a real system, we'd want a proper revocation chain
    console.warn('[Clipboard] Grant revocation not fully implemented');
  }

  /**
   * Export content (with permission check)
   */
  async export(entryHash: string): Promise<Uint8Array> {
    const check = await this.canExport(entryHash);
    if (!check.allowed) {
      throw new Error(check.reason ?? 'Export not permitted');
    }

    const result = await this.yank(entryHash);
    if (!result.success || !result.data) {
      throw new Error(result.error ?? 'Failed to retrieve content');
    }

    // TODO: Log export if method was 'admin' or 'grant'

    return result.data;
  }

  /**
   * Log an admin export (creates permanent audit record)
   */
  async logAdminExport(contentDag: string, reason: string): Promise<string> {
    const logContent: AdminExportLogContent = {
      admin: this.config.user.userId,
      contentDag,
      reason,
    };

    const logObj = createDAGObject<AdminExportLogContent>({
      type: 'clipboard.admin_export',
      creator: this.config.keyPair.publicKeyBase58,
      timestamp: Date.now(),
      parents: [],
      content: logContent,
    });
    const signedLog = signObject(logObj, this.config.keyPair);

    await this.config.dagStore.put(signedLog);
    return signedLog.hash;
  }

  /**
   * Get chunk download progress for an entry
   */
  async getProgress(entryHash: string): Promise<{
    complete: boolean;
    progress: number;
    missing: string[];
  }> {
    const obj = await this.config.dagStore.get(entryHash);
    if (!obj || !isClipboardEntry(obj)) {
      return { complete: false, progress: 0, missing: [entryHash] };
    }

    return getChunkStatus(obj.content.content.dag, this.config.dagStore);
  }

  /**
   * Handle incoming clipboard entry from sync
   */
  async handleIncomingEntry(entry: ClipboardEntry): Promise<void> {
    // Verify entry is valid clipboard entry
    if (!isClipboardEntry(entry)) return;

    // Check if we already have it
    const existing = await this.config.dagStore.get(entry.hash);
    if (existing) return;

    // Store entry
    await this.config.dagStore.put(entry);

    // Notify listener
    this.config.onNewEntry?.(entry);
  }

  /**
   * Get user stats
   */
  async getStats(): Promise<{
    totalPastes: number;
    totalBytes: number;
    byType: Record<string, number>;
  }> {
    const entries = await this.list({ limit: 1000 });
    const stats = {
      totalPastes: entries.length,
      totalBytes: 0,
      byType: {} as Record<string, number>,
    };

    for (const entry of entries) {
      stats.totalBytes += entry.content.content.size;

      // Group by type category
      const type = entry.content.content.type;
      let category = type;
      if (type.startsWith('image/')) category = 'image/*';
      else if (type.startsWith('video/')) category = 'video/*';
      else if (type.startsWith('audio/')) category = 'audio/*';
      else if (type.startsWith('text/x-')) category = 'text/x-*';
      else if (type.startsWith('text/')) category = 'text/*';

      stats.byType[category] = (stats.byType[category] ?? 0) + 1;
    }

    return stats;
  }

  /**
   * Delete a clipboard entry
   *
   * Creates a tombstone for the entry. Only the owner can delete their entries.
   */
  async delete(entryHash: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Get the entry
      const obj = await this.config.dagStore.get(entryHash);
      if (!obj || !isClipboardEntry(obj)) {
        return { success: false, error: 'Entry not found' };
      }

      // Verify ownership
      if (obj.creator !== this.config.keyPair.publicKeyBase58) {
        return { success: false, error: 'Only the owner can delete this entry' };
      }

      // Create and store tombstone
      const tombstoneData = createTombstoneContent(entryHash);
      const tombstoneObj = createDAGObject({
        type: tombstoneData.type,
        creator: this.config.keyPair.publicKeyBase58,
        timestamp: Date.now(),
        parents: [entryHash],
        content: tombstoneData.content,
      });
      const signedTombstone = signObject(tombstoneObj, this.config.keyPair);
      await this.config.dagStore.put(signedTombstone);

      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Delete failed',
      };
    }
  }

  /**
   * Get set of tombstoned entry hashes
   */
  private async getTombstonedHashes(): Promise<Set<string>> {
    const all = await this.config.dagStore.getAll();
    const tombstoned = new Set<string>();

    for (const obj of all) {
      if (isTombstone(obj)) {
        tombstoned.add(getTombstoneTarget(obj));
      }
    }

    return tombstoned;
  }
}

// ============================================================================
// Factory Function
// ============================================================================

/**
 * Create a clipboard manager
 */
export function createClipboard(config: ClipboardConfig): ClipboardManager {
  return new ClipboardManager(config);
}
